import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { priceComma } from "@common/splicePrice";
import Link from "next/link";
import { useRouter } from "next/router";
import { connect } from "react-redux";
import { useBrandProductsQuery } from "@hook/useBrandProductsQuery";
import { BANNER_PRODUCT_COOKIE_NAME, getCookie, setCookie } from "@common/auth";
import { useBannerStore } from "@store/useBannerStore";

// 애니메이션 정의
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// 스타일드 컴포넌트 정의
const BannerOverlay = styled.div`
  position: absolute;
  top: -235px;
  right: 0; /* 오른쪽 정렬 */
  width: 370px;
  max-width: 370px;
  z-index: 999;
  padding: 0;
  background-color: white;
  animation: ${fadeIn} 0.3s ease-in-out;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 5%;
    width: 100%;
    max-width: 90%;
    height: 100vh;
    z-index: 999;
  }
`;

const BannerContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    height: 100vh;
  }
`;

const BannerHeader = styled.div`
  background-color: #222;
  color: white;
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
`;

const BannerBody = styled.div`
  padding: 20px;

  @media (max-width: 768px) {
    flex: 1;
    overflow-y: auto;
    padding: 15px;
  }
`;

const BannerTitle = styled.div`
  font-size: 16px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CouponImage = styled.div`
  margin-bottom: 20px;
  text-align: center;

  img {
    max-width: 100%;
    border-radius: 10px;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    margin-bottom: 15px;
  }
`;

// 제품 그리드 스타일
const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-top: 15px;
  }
`;

// 제품 카드 컴포넌트
const ProductCard = styled.div`
  position: relative;
  background-color: white;
  border: 1px solid #ececec;
  border-radius: 15px;
  transition: 0.2s;
  overflow: hidden;

  &:hover {
    box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.05);
    border: 1px solid #bce3c9;
  }

  @media (max-width: 768px) {
    border-radius: 10px;
  }
`;

const ProductImgWrapper = styled.div`
  position: relative;
  background-color: #fff;
  overflow: hidden;
`;

const ProductImg = styled.a`
  overflow: hidden;
  display: block;

  & img {
    width: 100%;
    -webkit-transition: all 1s cubic-bezier(0, 0, 0.05, 1);
    transition: all 1s cubic-bezier(0, 0, 0.05, 1);

    &:hover {
      transform: scale3d(1.03, 1.03, 1.03) translateZ(0);
    }
  }
`;

const ProductContentWrapper = styled.div`
  padding: 0 10px 15px 10px;
`;

const ProductName = styled.a`
  width: 100%;
  height: 3rem;
  font-size: 14px;
  font-weight: 500;
  color: #253d4e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-wrap;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-wrap: break-word;

  &:hover {
    color: #3bb77e;
  }
`;

const ProductBottomWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding: 5px 0;
`;

const PriceWrapper = styled.div`
  width: 100%;
  display: flex;
`;

const PriceBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0;

  & > div {
    span:first-child {
      color: #dc3545;
      font-size: 14px;
      font-weight: 500;
      line-height: 24px;
      margin-right: 0.2rem;
    }

    span:last-child {
      font-size: 12px;
      line-height: 24px;
      font-weight: 500;
      text-decoration: line-through;
      color: #6c757d;
      opacity: 0.5;
    }
  }

  & h5 {
    font-size: 16px;
    font-weight: 600;
    color: #253d4e;
    margin: 0;
  }
`;

const ExitIntentBanner = ({ dummyList }) => {
  const [showBanner, setShowBanner] = useState(false);
  const router = useRouter();
  const { pageOut } = useBannerStore((s) => s);

  const { data: brandProducts } = useBrandProductsQuery({
    pcodeList: dummyList.out_product,
  });

  const handleClose = () => {
    // 배너 숨김 상태로 설정
    setShowBanner(false);

    const expiryDate = new Date();

    expiryDate.setHours(expiryDate.getHours() + 2);

    // 배너 숨김 유지 (2시간 동안)
    setCookie(BANNER_PRODUCT_COOKIE_NAME, "true", {
      path: "/",
      expires: expiryDate,
    });
  };

  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        const cookieValue = getCookie(BANNER_PRODUCT_COOKIE_NAME);
        if (cookieValue === "false" || !cookieValue) {
          setShowBanner(true);
          window.scrollTo({ top: 0 });
        }
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  const bannerCookie = getCookie(BANNER_PRODUCT_COOKIE_NAME);

  // 쿠키가 true이거나 showBanner가 false면 배너를 표시하지 않음
  const shouldShowBanner = showBanner && bannerCookie !== "true";

  const hasPageOut = Array.isArray(pageOut) && pageOut.length > 0;
  const pageOutData = hasPageOut ? pageOut[0] : null;

  return (
    <>
      {shouldShowBanner && pageOutData && (
        <BannerOverlay>
          <BannerContent>
            <BannerHeader>
              <BannerTitle>이런 제품은 어떠세요?</BannerTitle>
              <CloseButton onClick={handleClose}>✕</CloseButton>
            </BannerHeader>
            <BannerBody>
              <CouponImage>
                {pageOutData.P_FileUrl && (
                  <img
                    src={pageOutData.P_FileUrl}
                    alt={pageOutData.img_alt || "페이지 아웃 배너"}
                    onClick={() => {
                      if (pageOutData.link_url) {
                        router.push(pageOutData.link_url);
                      }
                    }}
                  />
                )}
              </CouponImage>

              <ProductGrid>
                {Array.isArray(brandProducts) &&
                  brandProducts.map((product,index) => (
                    <ProductCard key={index}>
                      <ProductImgWrapper>
                        <Link href={`/products/${product.pcode}`}>
                          <ProductImg>
                            <img src={product.main_image} alt={product.pname} />
                          </ProductImg>
                        </Link>
                      </ProductImgWrapper>
                      <ProductContentWrapper>
                        <Link href={`/products/${product.pcode}`}>
                          <ProductName>{product.pname}</ProductName>
                        </Link>
                        <ProductBottomWrapper>
                          <PriceWrapper>
                            <PriceBox>
                              {product.salePer > 0 && (
                                <div>
                                  <span>{product.salePer}%</span>
                                  <span>
                                    {product.price &&
                                      ` ${priceComma(product.price)}원`}
                                  </span>
                                </div>
                              )}
                              <div className={"d-flex"}>
                                <h5>{priceComma(product.discount_price)}원</h5>
                              </div>
                            </PriceBox>
                          </PriceWrapper>
                        </ProductBottomWrapper>
                      </ProductContentWrapper>
                    </ProductCard>
                  ))}
              </ProductGrid>
            </BannerBody>
          </BannerContent>
        </BannerOverlay>
      )}
    </>
  );
};

// Redux 상태를 props로 매핑
const mapStateToProps = (state) => ({
  dummyList: state.dummyList,
});

// connect 함수로 컴포넌트 연결 및 내보내기
export default connect(mapStateToProps, null)(ExitIntentBanner);
